import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, X } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "../content";
import { trackCalendlyOpen } from "../lib/analytics";

/**
 * Calendar / slot-booking CTA.
 *
 * Behaviour:
 *  - If BRAND.calendlyUrl is a real Calendly URL → opens an in-page modal
 *    with the Calendly inline embed.
 *  - If BRAND.calendlyUrl is null / unset / still a placeholder →
 *    smoothly scrolls to the consultation form and shows a friendly toast,
 *    so the CTA always does something useful while you finish setting up
 *    Calendly.
 *
 * To enable: open /app/frontend/src/content.js and set
 *   calendlyUrl: "https://calendly.com/<your-handle>/<event-slug>"
 */
const isRealCalendlyUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  if (!url.startsWith("https://calendly.com/")) return false;
  // Reject the documented placeholder
  if (url.includes("calendly.com/dietdotin")) return false;
  return true;
};

export default function CalendlyButton({
  variant = "primary",
  className = "",
  testId = "calendly-trigger",
  label = "Schedule on Calendar",
}) {
  const enabled = isRealCalendlyUrl(BRAND.calendlyUrl);
  const [open, setOpen] = useState(false);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const handleClick = () => {
    if (enabled) {
      trackCalendlyOpen({ source: testId });
      setOpen(true);
      return;
    }
    // Fallback: scroll to the form + friendly toast
    const el = document.getElementById("booking");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    toast(
      "Direct calendar booking is launching soon. Share your details below and we'll confirm a slot within 24 hours.",
      {
        duration: 6000,
        action: {
          label: "WhatsApp",
          onClick: () =>
            window.open(
              `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
                "Hi DietDotIn, I'd like to book a consultation.",
              )}`,
              "_blank",
            ),
        },
      },
    );
  };

  const cls = variant === "primary" ? "btn-primary" : "btn-ghost-glass";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid={testId}
        data-calendar-enabled={enabled ? "true" : "false"}
        className={`${cls} inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-heading font-semibold ${className}`}
      >
        <Calendar size={16} /> {label}
      </button>

      {open && enabled && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8 bg-[#0a0118]/85 backdrop-blur-md"
          data-testid="calendly-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="relative w-full max-w-4xl h-[80vh] max-h-[760px] glass-strong rounded-3xl overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="calendly-close"
              aria-label="Close calendar"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full glass-strong flex items-center justify-center text-white hover:text-[#27C6FF]"
            >
              <X size={16} />
            </button>
            <iframe
              src={`${BRAND.calendlyUrl}?embed_domain=dietdotin&embed_type=Inline&hide_landing_page_details=1&hide_gdpr_banner=1&background_color=140326&text_color=F5F3FF&primary_color=7A2BFF`}
              title="Schedule a DietDotIn consultation"
              className="w-full h-full border-0"
              loading="lazy"
              data-testid="calendly-iframe"
            />
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
